import type { SalesInvoicesInsertPOSInputSchema } from "@mono/validation/lib/Sales";
import { useStorage } from "@vueuse/core";
import { useContactStore } from "./contact";
import { useSettingsStore } from "./settings";
import { usePosStore } from "./pos";
import type { IndexedDBSchemas } from "@/composables/useIndexDB";

export type InvoiceSchema = IndexedDBSchemas["invoices"];
export type PendingInvoiceSchema = IndexedDBSchemas["pendingInvoices"];

const MAX_SYNC_INVOICES = 10;

const lastSyncedAt = useStorage<string>(
  "lastSyncedAt",
  new Date().toISOString()
);

export const useInvoiceStore = defineStore("invoice", () => {
  // states
  const invoices = ref<InvoiceSchema[]>([]);
  const pendingInvoices = ref<PendingInvoiceSchema[]>([]);

  const dataIsFetched = ref(false);

  // computes
  const allInvoices = computed(() =>
    invoices.value.concat(
      pendingInvoices.value.map((invoice) => ({
        ...invoice,
        isSynced: false,
      }))
    )
  );

  // composables
  const { cache } = useIndexDB();

  // fetchData handler
  const fetchData = async () => {
    const { settings } = useSettingsStore();
    const { logout } = useAuthStore();

    if (dataIsFetched.value === true) {
      return;
    }

    try {
      if (!settings) {
        throw new Error("Settings not found");
      }

      const [cachedInvoices, cachedPendingInvoices] = await Promise.allSettled([
        cache.invoices.getAll(),
        cache.pendingInvoices.getAll(),
      ]);

      if (cachedInvoices.status === "fulfilled") {
        invoices.value = cachedInvoices.value;
      }

      if (cachedPendingInvoices.status === "fulfilled") {
        pendingInvoices.value = cachedPendingInvoices.value;
      }

      dataIsFetched.value = true;
    } catch {
      logout();
    }
  };

  const generateZatcaInvoice = async (invoice: PendingInvoiceSchema) => {
    const { pointOfSale } = usePosStore();
    const { getProductPrices } = useProductStore();
    const { getContactInfo, getProductDetails } = useMapIdsToData();
    const { Decimal } = useCurrency();

    if (pointOfSale == null) {
      throw new Error("Point of sale not found");
    }

    const organization = pointOfSale.organization;
    const contact = getContactInfo(invoice.customerId);

    const invoiceProducts = invoice.salesInvoiceProducts.map((product) => {
      const prices = getProductPrices({
        originalUnitPrice: product.originalUnitPrice,
        actualUnitPrice: product.actualUnitPrice,
        quantity: product.quantity,
        taxPercent: product.taxPercent,
        taxIsIncluded: product.taxIsIncluded,
      });

      return {
        ...product,
        ...prices,
      };
    });

    const companySettings: CompanySettings = {
      tax_number: organization.taxNumber,
      crn_number: organization.crNumber!,
      business_category: "Mezan Technologies",
      title: "1100",
      email: organization.email!,
      common_name: organization.displayName!,
      organization_name: organization.displayName!,
      registered_address: organization.address!,
      organization_unit_name: pointOfSale.name,
    };

    const simplifiedCustomerInfo: SimplifiedCustomerInfo = {
      type: "customer",
      buyer_name: contact.contactName,
    };

    const invoiceTax = invoiceProducts.reduce(
      (acc, product) => acc.add(product.taxSubtotal),
      new Decimal(0)
    );

    const invoiceTotal = invoiceProducts
      .reduce(
        (acc, product) => acc.add(product.actualPriceSubtotal),
        new Decimal(0)
      )
      .add(invoiceTax);

    // init issue date
    const tzoffset = new Date().getTimezoneOffset() * 60000; // offset in milliseconds
    const localISODatetime = new Date(Date.now() - tzoffset)
      .toISOString()
      .slice(0, -5);

    const [issueDate, issueTime] = localISODatetime.split("T");

    const zatcaInvoice: Invoice = {
      customer_name: contact.contactName,
      invoice_type: "invoice",
      invoice_payment_type: "cash",
      items: invoiceProducts.map((product) => {
        const productDetails = getProductDetails(product.productId);
        const taxPercentAsMultiplier = new Decimal(product.taxPercent).div(100);

        const originalUnitPrice =
          product.taxIsIncluded === true
            ? new Decimal(product.originalUnitPrice)
                .div(taxPercentAsMultiplier.add(1))
                .toNumber()
            : product.originalUnitPrice;

        const actualUnitPrice =
          product.taxIsIncluded === true
            ? new Decimal(product.actualUnitPrice)
                .div(taxPercentAsMultiplier.add(1))
                .toNumber()
            : product.actualUnitPrice;

        return {
          product_name: productDetails.productName,
          original_price: originalUnitPrice,
          actual_price: actualUnitPrice,
          tax:
            product.taxPercent === 0
              ? ({
                  rate: 0,
                  reason: "Not subject to VAT",
                } as ZeroTax)
              : ({ rate: taxPercentAsMultiplier.toNumber() } as Tax),
          unit_qty: product.quantity,
        };
      }),
      tax_amount: invoiceTax.toNumber(),
      total: invoiceTotal.toNumber(),

      serial_number: invoice.reference!,
      uuid: invoice.id,
      issue_date: issueDate,
      issue_time: issueTime,
    };

    return {
      companySettings,
      customerInfo: simplifiedCustomerInfo,
      zatcaInvoice,
    };
  };

  // signInvoice handler
  const signInvoice = async (invoice: PendingInvoiceSchema) => {
    const { zatcaDevice, incrementZatcaDevice, isZatcaActive } = usePosStore();
    const { signInvoice: signInvoiceAPI } = usePrintHelper();

    if (isZatcaActive !== true || zatcaDevice == null) {
      throw new Error("Zatca is not active or connected");
    }

    const { zatcaInvoice, companySettings, customerInfo } =
      await generateZatcaInvoice(invoice);

    const signedInvoiceResult = await signInvoiceAPI({
      companySettings,
      customerInfo,
      zatcaInvoice,
      connectResult: zatcaDevice.connectResult,
      previousInvoiceData: {
        previous_invoice_counter_number: zatcaDevice.lastInvoiceCounter,
        previous_invoice_hash: zatcaDevice.lastInvoiceHash,
      },
    });

    if (signedInvoiceResult == null) {
      throw new Error("Failed to sign invoice");
    }

    const { qr, invoice_hash, signed_invoice_string, egsInfoUuid } =
      signedInvoiceResult;

    await incrementZatcaDevice({ invoiceHash: invoice_hash });

    return {
      qr,
      invoiceHash: invoice_hash,
      signedInvoice: signed_invoice_string,
      egsInfoUuid,
    };
  };

  const reSignInvoice = async (invoiceId: string) => {
    const { zatcaSettings } = usePosStore();

    const pendingInvoice = await cache.pendingInvoices.get(invoiceId);

    if (pendingInvoice == null) {
      throw new Error("Invoice not found");
    }

    const zatcaInvoice =
      zatcaSettings.isProduction === true
        ? pendingInvoice.productionZatcaInvoice
        : pendingInvoice.simulationZatcaInvoice;

    // check if the invoice is already signed
    if (zatcaInvoice != null) {
      return { success: false };
    }

    const { qr, invoiceHash, signedInvoice, egsInfoUuid } =
      await signInvoice(pendingInvoice);

    if (zatcaSettings.isProduction === true) {
      pendingInvoice.productionZatcaInvoice = {
        qr,
        invoiceHash,
        signedInvoice,
        egsInfoUuid,
      };
    } else {
      pendingInvoice.simulationZatcaInvoice = {
        qr,
        invoiceHash,
        signedInvoice,
        egsInfoUuid,
      };
    }

    await cache.pendingInvoices.updateOne(
      invoiceId,
      JSON.parse(JSON.stringify(pendingInvoice))
    );
    pendingInvoices.value = pendingInvoices.value.map((invoice) =>
      invoice.id === invoiceId ? pendingInvoice : invoice
    );

    return { success: true };
  };

  // submitInvoice handler
  const submitInvoice = async (invoice: PendingInvoiceSchema) => {
    const { zatcaSettings } = usePosStore();
    const { silentPrint } = usePrint();

    const data: PendingInvoiceSchema = {
      ...invoice,
    };

    try {
      const { qr, invoiceHash, signedInvoice, egsInfoUuid } =
        await signInvoice(invoice);

      if (zatcaSettings.isProduction === true) {
        data.productionZatcaInvoice = {
          qr,
          invoiceHash,
          signedInvoice,
          egsInfoUuid,
        };
      } else {
        data.simulationZatcaInvoice = {
          qr,
          invoiceHash,
          signedInvoice,
          egsInfoUuid,
        };
      }
    } catch (error) {
      console.error("Error submitting invoice:", error);
    }

    // cache invoice
    await cache.pendingInvoices.add(JSON.parse(JSON.stringify(data)));

    pendingInvoices.value.push(data);

    silentPrint({ invoice: data });

    // sync invoices after 15 seconds
    setTimeout(async () => {
      await syncInvoices();
    }, 1000 * 15);
  };

  // State reconciliation helper
  const reconcileInvoiceStates = async () => {
    const [cachedInvoices, cachedPending] = await Promise.all([
      cache.invoices.getAll(),
      cache.pendingInvoices.getAll(),
    ]);

    // Remove any duplicates between stores
    const syncedIds = new Set(cachedInvoices.map((inv) => inv.id));
    const pendingInvoicesToRemove = cachedPending.filter((inv) =>
      syncedIds.has(inv.id)
    );

    await cache.pendingInvoices.deleteMany(
      pendingInvoicesToRemove.map((inv) => inv.id)
    );

    pendingInvoices.value = pendingInvoices.value.filter(
      (inv) => !pendingInvoicesToRemove.some((p) => p.id === inv.id)
    );
  };

  const syncInvoices = async () => {
    if (pendingInvoices.value.length === 0) {
      return;
    }

    // run the online check
    const { isOnline } = useNetworkCheck();
    await isOnline();

    const { trpcClient } = useTrpcClient();
    const { syncContacts, pendingContacts } = useContactStore();
    const { settings } = useSettingsStore();
    const {
      posReference,
      updateCashAccountLastRunningTotal,
      zatcaSettings,
      isZatcaActive,
    } = usePosStore();
    const { isConnected } = usePrintHelper();

    if (!settings || !posReference) {
      throw new Error("Settings not found");
    }

    // sync contacts before syncing invoices, if pendingContacts exists
    if (pendingContacts.length > 0) {
      await syncContacts();
    }

    // Create a shallow copy instead of deep clone to avoid memory issues
    const pendingInvoiceClone = [...pendingInvoices.value];

    let pendingInvoicesToSync: PendingInvoiceSchema[] = [];

    if (isZatcaActive === true) {
      for (let i = 0; i < pendingInvoiceClone.length; i++) {
        const invoice = pendingInvoiceClone[i];

        if (
          zatcaSettings.isProduction === true
            ? invoice.productionZatcaInvoice != null
            : invoice.simulationZatcaInvoice != null
        ) {
          pendingInvoicesToSync.push(invoice);
          continue;
        }

        if (isConnected.value !== true) {
          // skip attempting to sign if printhelper is not connected
          continue;
        }

        try {
          const { qr, invoiceHash, signedInvoice, egsInfoUuid } =
            await signInvoice(invoice);

          if (zatcaSettings.isProduction === true) {
            invoice.productionZatcaInvoice = {
              qr,
              invoiceHash,
              signedInvoice,
              egsInfoUuid,
            };
          } else {
            invoice.simulationZatcaInvoice = {
              qr,
              invoiceHash,
              signedInvoice,
              egsInfoUuid,
            };
          }

          pendingInvoicesToSync.push(invoice);
        } catch (error) {
          console.error("Error signing invoice:", error);
          // skip the invoice
          continue;
        }
      }
    } else {
      pendingInvoicesToSync = pendingInvoiceClone;
    }

    try {
      // process invoices in chunks with atomic updates
      for (
        let i = 0;
        i < pendingInvoicesToSync.length;
        i += MAX_SYNC_INVOICES
      ) {
        const chunk = pendingInvoicesToSync.slice(i, i + MAX_SYNC_INVOICES);

        // wrap each chunk in a transaction-like operation
        await Promise.all([
          (async () => {
            const { invoicesIds } =
              await trpcClient.salesInvoices.posCreateMany.mutate({
                data: chunk as SalesInvoicesInsertPOSInputSchema[],
              });

            const newInvoices = chunk.map((invoice, index) => ({
              ...invoice,
              id: invoice.id ?? invoicesIds[index],
            }));

            // atomic update of both stores for this chunk
            await Promise.all([
              cache.invoices.addMany(newInvoices),
              cache.pendingInvoices.deleteMany(chunk.map((inv) => inv.id)),
            ]);

            // update in-memory state
            invoices.value = invoices.value.concat(newInvoices);
            pendingInvoices.value = pendingInvoices.value.filter(
              (inv) => !chunk.some((chunkInv) => chunkInv.id === inv.id)
            );
          })(),
        ]);
      }
    } catch (error) {
      console.error("Error syncing invoices:", error);
      // trigger state reconciliation on error
      await reconcileInvoiceStates();
      throw error; // re-throw to be caught by the finally block
    } finally {
      // update POS counters and final state
      await Promise.all([
        updateCashAccountLastRunningTotal(),
        trpcClient.pointOfSales.posUpdateOne.mutate({
          id: settings.posId,
          data: {
            posInvoicesReferenceCounter:
              posReference.posInvoicesReferenceCounter,
            posReceiptsReferenceCounter:
              posReference.posReceiptsReferenceCounter,
          },
        }),
      ]);
      lastSyncedAt.value = new Date().toISOString();
    }
  };

  // clearDuplicatePendingInvoices handler
  const clearDuplicatePendingInvoices = async () => {
    await syncInvoices();

    // 2. build up a new pendingInvoices array, that have no duplicates
    const duplicatesSet = new Set();

    const newPendingInvoices: PendingInvoiceSchema[] = [];

    for (let i = 0; i < pendingInvoices.value.length; i++) {
      const invoice = pendingInvoices.value[i];

      const key = `${invoice.reference}-${invoice.issueDate}-${
        invoice.customerId
      }-${invoice.salesInvoiceProducts.reduce(
        (acc, product) =>
          `${acc}-${product.productId}-${product.quantity}-${product.actualUnitPrice}`,
        ""
      )}-${invoice.salesReceiptInvoiceUsages?.length ?? 0}`;

      if (duplicatesSet.has(key)) {
        continue;
      }

      duplicatesSet.add(key);
      newPendingInvoices.push(invoice);
    }

    // if the length of newPendingInvoices is equal to the length of pendingInvoices, return early (no duplicates found)
    if (newPendingInvoices.length === pendingInvoices.value.length) {
      return;
    }

    // 3. update the pendingInvoices cache

    // getAllKeys of the pendingInvoices
    const keys = await cache.pendingInvoices.getAllKeys();

    // add the unsyncedInvoices to pendingInvoices
    await cache.pendingInvoices.addMany(newPendingInvoices);

    // remove the old keys
    await cache.pendingInvoices.deleteMany(keys);

    pendingInvoices.value = newPendingInvoices;
  };

  // deletePendingInvoice handler
  const deletePendingInvoice = async (invoice: {
    reference: string;
    createdAt: string;
    customerId: string;
    salesInvoiceProducts: PendingInvoiceSchema["salesInvoiceProducts"];
    salesReceiptInvoiceUsagesLength: number;
  }) => {
    // if issueDate not older than 2 days, return early
    if (
      invoice.createdAt >
      new Date(new Date().getTime() - 2 * 24 * 60 * 60 * 1000).toISOString()
    ) {
      return;
    }

    const key = `${invoice.reference}-${invoice.createdAt}-${
      invoice.customerId
    }-${invoice.salesInvoiceProducts.reduce(
      (acc, product) =>
        `${acc}-${product.productId}-${product.quantity}-${product.actualUnitPrice}`,
      ""
    )}-${invoice.salesReceiptInvoiceUsagesLength ?? 0}`;

    const newPendingInvoices = pendingInvoices.value.filter(
      (pendingInvoice) => {
        const pendingKey = `${pendingInvoice.reference}-${
          pendingInvoice.createdAt
        }-${
          pendingInvoice.customerId
        }-${pendingInvoice.salesInvoiceProducts.reduce(
          (acc, product) =>
            `${acc}-${product.productId}-${product.quantity}-${product.actualUnitPrice}`,
          ""
        )}-${pendingInvoice.salesReceiptInvoiceUsages?.length ?? 0}`;

        return pendingKey !== key;
      }
    );

    // if the length of newPendingInvoices is equal to the length of pendingInvoices, return early (no duplicates found)
    if (newPendingInvoices.length === pendingInvoices.value.length) {
      return;
    }

    // getAllKeys of the pendingInvoices
    const keys = await cache.pendingInvoices.getAllKeys();

    // add the unsyncedInvoices to pendingInvoices
    await cache.pendingInvoices.addMany(newPendingInvoices);

    // remove the old keys
    await cache.pendingInvoices.deleteMany(keys);

    pendingInvoices.value = newPendingInvoices;
  };

  // clear handler
  const clear = async () => {
    const { settings } = useSettingsStore();

    if (!settings) return;

    await syncInvoices();

    if (allInvoices.value.length > 0) {
      await cache.dumpInvoices.add({
        posId: `${settings.posId}-${new Date().toISOString()}`,
        invoices: JSON.parse(JSON.stringify(allInvoices.value)),
      });
    }

    await Promise.all([cache.invoices.clear(), cache.pendingInvoices.clear()]);
    invoices.value = [];
    pendingInvoices.value = [];
  };

  // init handler
  const init = async () => {
    // fetch invoices first not in parallel to avoid racing condition
    // cuz both mutate the same state
    await fetchData();
    await syncInvoices();
  };

  return {
    // getters
    invoices,
    pendingInvoices,
    allInvoices,
    lastSyncedAt,
    // setters
    clear,
    init,
    fetchData,
    submitInvoice,
    syncInvoices,
    clearDuplicatePendingInvoices,
    deletePendingInvoice,
    reSignInvoice,
  };
});
